import { useTraduccion } from '@tallerya/i18n';
import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { esDelEquipo } from './esDelEquipo';

// La puerta de la consola entera, no de una ruta.
//
// En el admin las guardas van por ruta porque hay pantallas que ve cualquiera del
// taller y otras que piden un permiso. Acá no: **toda la consola es del equipo**,
// así que no se dibuja nada —ni la navegación— hasta saber que quien mira lo es.
// Una ruta que alguien olvide proteger no puede existir si no hay rutas sin
// proteger.
//
// Los estados son los del admin más uno:
//
//   resolviendo   la sesión todavía no se sabe (al recargar, mientras se lee
//                 sessionStorage). Sin esto, parpadea «entra» y después el contenido
//   error         Keycloak devolvió un error al volver
//   sin sesión    nunca entró, o se le venció y no se pudo renovar
//   ajeno         entró, y no es del equipo. Keycloak ya debería haberlo frenado:
//                 ver `esDelEquipo`
//   adentro       se dibuja
//
// **No se manda a Keycloak solo.** Pedir el ingreso automáticamente al abrir la
// página se ve más prolijo, pero si Keycloak niega la entrada y devuelve el error
// a la consola, la consola volvería a mandarlo, en un bucle. Un botón lo corta.

interface Props {
  children: ReactNode;
}

export function Acceso({ children }: Props) {
  const { t } = useTraduccion();
  const auth = useAuth();

  if (auth.isLoading) {
    return <p className="entrada">{t('app.cargando')}</p>;
  }

  if (auth.error) {
    return (
      <Puerta titulo={t('plataforma.acceso.error.titulo')} texto={t('plataforma.acceso.error.texto')}>
        <button type="button" onClick={() => void auth.signinRedirect()}>
          {t('app.reintentar')}
        </button>
      </Puerta>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Puerta titulo={t('plataforma.acceso.sinSesion.titulo')} texto={t('plataforma.acceso.sinSesion.texto')}>
        <button type="button" onClick={() => void auth.signinRedirect()}>
          {t('plataforma.sesion.entrar')}
        </button>
      </Puerta>
    );
  }

  if (!esDelEquipo(auth.user?.profile)) {
    return (
      <Puerta titulo={t('plataforma.acceso.ajeno.titulo')} texto={t('plataforma.acceso.ajeno.texto')}>
        {/* Salir de verdad, en Keycloak: con la sesión abierta allá, volver a
            «Entrar» traería de vuelta el mismo usuario sin preguntar. */}
        <button type="button" onClick={() => void auth.signoutRedirect()}>
          {t('plataforma.sesion.salir')}
        </button>
      </Puerta>
    );
  }

  return <>{children}</>;
}

interface PuertaProps {
  titulo: string;
  texto: string;
  children: ReactNode;
}

function Puerta({ titulo, texto, children }: PuertaProps) {
  const { t } = useTraduccion();

  return (
    <main className="puerta">
      <p className="marca">{t('plataforma.marca')}</p>
      <h1>{titulo}</h1>
      <p className="entrada">{texto}</p>
      {children}
    </main>
  );
}
